import { useState, useEffect, useRef } from "react";
import { fetchClientsMeta } from "../utils/api";

/* ═══════════════════════════════════════════════════════════════
   ClientPicker — Searchable input for internal Patriot staff to
   switch between client dashboards. Type to filter; click a result
   to select. X clears the selection. Fetches from GET /api/clients.
   ═══════════════════════════════════════════════════════════════ */

const T = {
  bg: "#0b1f3a",
  panel: "#112b4d",
  border: "rgba(255,255,255,0.08)",
  text: "#edf4ff",
  muted: "#a8bddc",
  blue: "#44a6ff",
};

const scoreColor = (s) =>
  s >= 70 ? "#53c46d" : s >= 50 ? "#f3b53f" : "#e14d57";

export default function ClientPicker({ onSelect, initialClientId }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  // Default list is customers the portal serves (backend scope=entitled);
  // the toggle is the Patriot escape hatch for finding a record outside
  // that universe — an exception, not a mechanism.
  const [showAll, setShowAll] = useState(false);
  // True when the backend failed OPEN (entitlement read unavailable) —
  // the list is the full universe and says so, rather than pretending
  // it's the filtered one.
  const [scopeFallback, setScopeFallback] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  // The ?client= URL restore must run once, on the FIRST load only —
  // a scope-toggle refetch must not re-fire it.
  const restoredRef = useRef(false);

  /* ── Fetch client list on mount and on scope toggle ── */
  useEffect(() => {
    fetchClientsMeta(showAll ? { scope: "all" } : {})
      .then(({ clients: data, scopeApplied }) => {
        setScopeFallback(scopeApplied === "all_fallback");
        if (!Array.isArray(data) || data.length === 0) return;

        // Deduplicate by customer_name (case-insensitive, trimmed) —
        // keep the first occurrence. Dataverse can have duplicate
        // company records that we don't want to surface in the picker.
        const seen = new Set();
        const deduped = [];
        for (const c of data) {
          const key = (c.customer_name || "").trim().toLowerCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          deduped.push(c);
        }

        // Sort alphabetically by customer_name (case-insensitive,
        // locale-aware so accented characters sort sensibly).
        deduped.sort((a, b) =>
          (a.customer_name || "").localeCompare(b.customer_name || "", undefined, {
            sensitivity: "base",
          })
        );

        setClients(deduped);

        // Fresh load defaults to NO selection — Patriot users always have a
        // specific customer in mind, so auto-selecting the first one just
        // flashed a random customer's data. The only exception is restoring a
        // previously-selected customer from the URL (?client={customer_id},
        // passed down as initialClientId) after a page refresh. This restore
        // runs exactly once, here, after the list has loaded — keeping it
        // deterministic and avoiding the customer-switch race. A stale/invalid
        // id simply finds no match and leaves the picker unselected.
        if (initialClientId && !restoredRef.current) {
          restoredRef.current = true;
          const match = deduped.find((c) => c.customer_id === initialClientId);
          if (match) {
            setSelected(match);
            onSelect(match);
          }
        }
      })
      .catch((err) => console.warn("Failed to fetch client list:", err.message));
    // onSelect is stable (useCallback in parent) and initialClientId is read
    // once on first load (restoredRef) — intentionally not in deps.
    // eslint-disable-next-line
  }, [showAll]);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        // If user typed a query but didn't pick anything, revert input to
        // the currently selected client's name.
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  /* ── Filtering (case-insensitive partial match on customer_name) ── */
  const q = query.trim().toLowerCase();
  const filtered = q
    ? clients.filter((c) => (c.customer_name || "").toLowerCase().includes(q))
    : clients;

  const handleSelect = (client) => {
    setSelected(client);
    setQuery("");
    setOpen(false);
    onSelect(client);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    setQuery("");
    setOpen(true);
    onSelect(null);
    // Focus the input so the user can immediately search again
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  // Show the selected client's name in the input ONLY when not actively typing
  const inputValue = open ? query : selected ? selected.customer_name : query;

  return (
    // 260px preferred (flexBasis, matching the old fixed width) but
    // shrinkable to 170 so the header can compress on laptop widths before
    // the document overflows. The dropdown tracks the wrapper width; client
    // names in it already ellipsise.
    <div ref={wrapRef} style={{ position: "relative", flex: "0 1 260px", minWidth: 170 }}>
      {/* ── Search input ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 8,
          border: `1px solid ${open ? T.blue : T.border}`,
          background: "rgba(255,255,255,0.04)",
          transition: "border-color 0.15s",
        }}
      >
        {/* Magnifying glass icon */}
        <span
          style={{
            fontSize: 12,
            color: T.muted,
            lineHeight: 1,
            display: "inline-block",
          }}
          aria-hidden="true"
        >
          {"\u{1F50D}"}
        </span>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          placeholder="Search clients..."
          onFocus={() => {
            setOpen(true);
            // If a client is selected, clear the visual text so typing filters
            // against the full list rather than the selected name.
            if (selected) setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            background: "transparent",
            color: T.text,
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            padding: 0,
            minWidth: 0,
          }}
        />
        {/* Score badge next to selected client (hidden while typing) */}
        {selected && !open && selected.current_score != null && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: scoreColor(selected.current_score),
            }}
          >
            {selected.current_score}%
          </span>
        )}
        {/* Clear (X) button */}
        {selected && (
          <button
            onClick={handleClear}
            aria-label="Clear selection"
            title="Clear selection"
            style={{
              border: "none",
              background: "transparent",
              color: T.muted,
              cursor: "pointer",
              fontSize: 14,
              lineHeight: 1,
              padding: "0 2px",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = T.text)}
            onMouseLeave={(e) => (e.currentTarget.style.color = T.muted)}
          >
            {"\u2715"}
          </button>
        )}
      </div>

      {/* ── Results dropdown ── */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            left: 0,
            background: T.panel,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          <div style={{ maxHeight: 280, overflowY: "auto", padding: "4px 0" }}>
            {filtered.length === 0 && (
              <div
                style={{
                  padding: "12px 14px",
                  fontSize: 12,
                  color: T.muted,
                }}
              >
                No clients found
              </div>
            )}
            {filtered.map((c) => {
              const isSel = selected && selected.customer_id === c.customer_id;
              return (
                <div
                  key={c.customer_id}
                  onClick={() => handleSelect(c)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 14px",
                    cursor: "pointer",
                    background: isSel ? "rgba(68,166,255,0.12)" : "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSel)
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.04)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSel)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: T.text,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.customer_name}
                    </div>
                  </div>
                  {c.current_score != null && (
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: scoreColor(c.current_score),
                        marginLeft: 10,
                      }}
                    >
                      {c.current_score}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Scope footer — Patriot escape hatch (exception, not
                 mechanism): default list is customers the portal serves;
                 the checkbox widens to the full universe. ── */}
          <div
            style={{
              borderTop: `1px solid ${T.border}`,
              padding: "7px 14px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                color: T.muted,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                style={{ accentColor: T.blue, cursor: "pointer" }}
              />
              Show all customers
            </label>
            {scopeFallback && !showAll && (
              <div style={{ fontSize: 11, color: "#f3b53f", marginTop: 4 }}>
                Showing all — entitlement read unavailable
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
